import React, { useEffect, useState } from 'react';
import { Save, Loader2, Bike, Car, Truck, Phone, MapPin, Hash, IdCard, Mail } from 'lucide-react';
import { driverApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import DriverMarketplacePanel from './DriverMarketplacePanel';
import DriverCoverageZones from './DriverCoverageZones';
import SuperDriverPanel from './SuperDriverPanel';
import PhotoDocPicker from './PhotoDocPicker';
import CourierApplicationPanel from './CourierApplicationPanel';
import api from '../services/api';

const VEHICLES = [
  { v: 'Bike', icon: Bike }, { v: 'Scooter', icon: Bike }, { v: 'Car', icon: Car }, { v: 'Van', icon: Truck },
];

const DriverProfile = () => {
  const [driver, setDriver] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    driverApi.me().then((d) => {
      setDriver(d);
      setForm({
        phone: d.phone || '',
        whatsapp_number: d.whatsapp_number || '',
        vehicle_type: d.vehicle_type || 'Bike',
        vehicle_plate: d.vehicle_plate || '',
        license_number: d.license_number || '',
        photo: d.photo || '',
        service_area: d.service_area || '',
        postal_code: d.postal_code || '',
        home_address: d.home_address || '',
        license_photo_url: d.license_photo_url || '',
        license_photo_back_url: d.license_photo_back_url || '',
        registration_photo_url: d.registration_photo_url || '',
        insurance_doc_url: d.insurance_doc_url || '',
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try { const d = await driverApi.updateMe(form); setDriver(d); toast.success('Profile updated'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // Persist a single field as soon as a new photo is uploaded — reassures the
  // driver that documents are saved without having to remember to hit "Save".
  const savePhotoField = async (field, url) => {
    setForm((prev) => ({ ...prev, [field]: url }));
    try {
      const d = await driverApi.updateMe({ [field]: url });
      setDriver(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save photo');
    }
  };

  if (!form) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Profile</h1>
          <p className="text-slate-500 mt-1">Keep your driver details up to date.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="driver-profile-save">
          <Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field icon={Phone} label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <Field icon={Phone} label="WhatsApp number (order updates)" value={form.whatsapp_number} onChange={(v) => setForm({ ...form, whatsapp_number: v })} />
            <Field icon={MapPin} label="Service area" value={form.service_area} onChange={(v) => setForm({ ...form, service_area: v })} />
            <Field icon={MapPin} label="Home address (used for zone matching)" value={form.home_address} onChange={(v) => setForm({ ...form, home_address: v })} full />
            <Field icon={Mail} label="Postal / ZIP code" value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v.toUpperCase() })} placeholder="e.g. K1P 5A4" />
            <Field icon={Hash} label="Vehicle plate" value={form.vehicle_plate} onChange={(v) => setForm({ ...form, vehicle_plate: v })} />
            <Field icon={IdCard} label="License number" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Vehicle type</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {VEHICLES.map((v) => (
                <button type="button" key={v.v} onClick={() => setForm({ ...form, vehicle_type: v.v })}
                  className={`h-12 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium ${form.vehicle_type === v.v ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                  data-testid={`driver-vehicle-${v.v.toLowerCase()}`}>
                  <v.icon className="h-4 w-4" />{v.v}
                </button>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Verification documents</h3>
                <p className="text-xs text-slate-500 mt-0.5">Snap with your phone camera or upload from your gallery. Photos are auto-resized under 5 MB.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="driver-documents-grid">
              <PhotoDocPicker
                label="Driver license (front)"
                value={form.license_photo_url}
                onChange={(url) => savePhotoField('license_photo_url', url)}
                testIdPrefix="driver-license-front"
                cameraTitle="License — front"
                compact
              />
              <PhotoDocPicker
                label="Driver license (back)"
                value={form.license_photo_back_url}
                onChange={(url) => savePhotoField('license_photo_back_url', url)}
                testIdPrefix="driver-license-back"
                cameraTitle="License — back"
                compact
              />
              <PhotoDocPicker
                label="Vehicle registration"
                value={form.registration_photo_url}
                onChange={(url) => savePhotoField('registration_photo_url', url)}
                testIdPrefix="driver-registration"
                cameraTitle="Vehicle registration"
                compact
              />
              <PhotoDocPicker
                label="Insurance document"
                value={form.insurance_doc_url}
                onChange={(url) => savePhotoField('insurance_doc_url', url)}
                testIdPrefix="driver-insurance"
                cameraTitle="Insurance document"
                compact
              />
            </div>
          </div>

          {/* COURIER application — business registration + insurance for admin approval */}
          <CourierApplicationPanel driver={driver} onChange={async () => setDriver(await driverApi.me())} />
        </div>

        <aside className="bg-white border border-slate-200 rounded-2xl p-5">
          {driver.is_super_driver && (
            <div className="mb-3 -mt-1 flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-lime-400 text-white px-3 py-2 shadow" data-testid="super-driver-badge">
              <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
              <span className="font-bold text-sm tracking-wide uppercase">Super Driver</span>
              <span className="ml-auto text-xs opacity-90">City-wide access</span>
            </div>
          )}
          <div className="text-xs text-slate-600 font-medium mb-2">Profile photo</div>
          <PhotoDocPicker
            label={null}
            value={form.photo}
            onChange={(url) => savePhotoField('photo', url)}
            testIdPrefix="driver-avatar"
            cameraTitle="Take a selfie"
          />
          <div className="mt-4 flex items-center gap-3">
            <div>
              <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5">
                {driver.name}
                {driver.is_courier && (
                  <span className="px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[9px] font-bold uppercase tracking-wider" data-testid="driver-profile-courier-badge" title="Verified business + insurance — eligible for same-day packages, courier & business logistics">COURIER</span>
                )}
              </div>
              <div className="text-xs text-slate-500">{driver.email}</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-slate-600 space-y-1.5 border-t border-slate-100 pt-4">
            <Row k="Status" v={<span className="capitalize font-medium text-emerald-700">{driver.status}</span>} />
            <Row k="Deliveries" v={driver.deliveries_count ?? 0} />
            <Row k="Rating" v={(driver.rating ?? 5).toFixed(1)} />
            <Row k="Earnings" v={`$${(driver.earnings_total ?? 0).toFixed(2)}`} />
          </div>
        </aside>
      </div>

      <SuperDriverPanel driver={driver} onChange={async () => {
        const d = await driverApi.me();
        setDriver(d);
      }} />
      <DriverCoverageZones />
      <DriverMarketplacePanel />
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, full, placeholder }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="relative mt-1">
      {Icon && <Icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
      <Input className={Icon ? 'pl-9 h-10' : 'h-10'} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  </div>
);

const Row = ({ k, v }) => (<div className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-900">{v}</span></div>);

export default DriverProfile;
